"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Check, Search, X } from "lucide-react";
import { userData } from "@/Redux/apis/userData";
import { getApiErrorMessage } from "@/utils/apiErrors";
import { useAddRepCompanyMutation, useRemoveRepCompanyMutation, useRepCompaniesQuery } from "@/Redux/apis/reps";
const BASE_URL = process.env.NEXT_PUBLIC_BASE_URL;
const PAGE_SIZE = 30;
const SkeletonRows = t0 => {
  const $ = _c(2);
  const {
    count
  } = t0;
  let t1;
  if ($[0] !== count) {
    t1 = <div aria-hidden="true">{Array.from({
        length: count
      }, _temp)}</div>;
    $[0] = count;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const searchError = (error, fallback) => getApiErrorMessage({
  status: error?.response?.status,
  data: error?.response?.data
}, fallback);

// Lets a rep add or remove the companies they represent. Mirrors the admin
// panel picker: search on top, the full company list below with an Add or
// Added state per row.
const RepCompaniesManager = ({
  onClose,
  autoFocus = true
}) => {
  const dispatch = useDispatch();
  const token = useSelector(state => state?.token?.token);
  const companiesQuery = useRepCompaniesQuery(undefined, {
    refetchOnMountOrArgChange: true
  });
  const [addCompany] = useAddRepCompanyMutation();
  const [removeCompany] = useRemoveRepCompanyMutation();

  // Adds and removes show immediately; the server list replaces them once the
  // request finishes, and a failed request puts the previous list back.
  const [companies, setCompanies] = useState(() => companiesQuery?.data?.companies || []);
  useEffect(() => {
    if (companiesQuery?.data?.companies) setCompanies(companiesQuery.data.companies);
  }, [companiesQuery?.data]);
  const inFlight = useRef(new Set());
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [failure, setFailure] = useState("");
  const requestRef = useRef(0);
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const loadPage = useCallback(async (search, pageNumber) => {
    const requestId = ++requestRef.current;
    setLoading(true);
    setFailure("");
    try {
      const params = new URLSearchParams({
        page: String(pageNumber),
        limit: String(PAGE_SIZE)
      });
      if (search) params.set("search", search);
      const response = await axios.get(`${BASE_URL}/content/get-manufacturers?${params.toString()}`);
      if (requestId !== requestRef.current) return;
      const rows = response?.data?.manufacturers || [];
      setOptions(previous => {
        const merged = pageNumber === 1 ? rows : [...previous, ...rows];
        const seen = new Set();
        return merged.filter(row => !seen.has(String(row._id)) && seen.add(String(row._id)));
      });
      setPage(pageNumber);
      setTotalPages(response?.data?.totalPages || 1);
    } catch (error) {
      if (requestId === requestRef.current) setFailure(searchError(error, "Couldn't load companies. Please try again."));
    } finally {
      if (requestId === requestRef.current) setLoading(false);
    }
  }, []);

  // Reload from the first page whenever the search changes.
  useEffect(() => {
    const search_0 = query.trim();
    const timer = setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      loadPage(search_0, 1);
    }, search_0 ? 300 : 0);
    return () => clearTimeout(timer);
  }, [query, loadPage]);

  // Load the next page when the bottom of the list scrolls into view.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || loading || failure || page >= totalPages) return;
    const observer = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting) loadPage(query.trim(), page + 1);
    }, {
      root: scrollRef.current,
      rootMargin: "120px"
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, failure, page, totalPages, query, loadPage, options.length]);

  // The navbar reads the rep's companies from the logged-in user.
  const refreshUser = () => dispatch(userData({
    token,
    type: "reps"
  }));
  const run = async (id, change, action, successMessage) => {
    if (inFlight.current.has(id)) return;
    inFlight.current.add(id);
    const previous_0 = companies;
    setCompanies(change);
    try {
      const result = await action().unwrap();
      if (result?.companies) setCompanies(result.companies);
      toast.success(successMessage);
      refreshUser();
    } catch (error_0) {
      setCompanies(previous_0);
      toast.error(error_0?.data?.message || "Could not update your companies. Please try again.");
    } finally {
      inFlight.current.delete(id);
    }
  };
  const handleAdd = manufacturer => {
    const id_0 = String(manufacturer._id);
    run(id_0, list => [...list, {
      id: id_0,
      name: manufacturer.name,
      primary: !list.length
    }], () => addCompany(id_0), `${manufacturer.name} added`);
  };
  const handleRemove = company => run(company.id, list_0 => list_0.filter(item => item.id !== company.id), () => removeCompany(company.id), `${company.name} removed`);
  const addedIds = new Set(companies.map(company_0 => company_0.id));
  const search_1 = query.trim();
  return <div className="flex flex-col h-full min-h-0 font-poppins normal-case">
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 flex-shrink-0">
        <Search size={17} className="text-gray-400 flex-shrink-0" aria-hidden="true" />
        <input type="search" value={query} autoFocus={autoFocus} onChange={event => setQuery(event.target.value)} placeholder="Search companies…" aria-label="Search companies" autoComplete="off" className="flex-1 min-w-0 border-none outline-none text-sm bg-transparent" />
        {onClose && <button type="button" onClick={onClose} aria-label="Close company menu" className="p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100">
            <X size={17} />
          </button>}
      </div>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto py-1">
        {!search_1 && companiesQuery.isLoading && <div className="pb-1 mb-1 border-b border-gray-100">
            <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Your companies</div>
            <SkeletonRows count={1} />
          </div>}
        {!search_1 && !companiesQuery.isLoading && companies.length > 0 && <div className="pb-1 mb-1 border-b border-gray-100">
            <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Your companies</div>
            {companies.map(company_1 => <div key={company_1.id || "legacy"} className="flex items-center gap-2 px-3 py-2 text-sm">
                <span title={company_1.name} className="flex-1 min-w-0 truncate capitalize text-gray-900">{company_1.name}</span>
                {company_1.id && companies.length > 1 ? <button type="button" onClick={() => handleRemove(company_1)} className="flex-shrink-0 text-xs font-medium text-gray-500 hover:text-red-600">
                    Remove
                  </button> : <Check size={16} className="text-[#354EF0] flex-shrink-0" aria-label="Added" />}
              </div>)}
            <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">All companies</div>
          </div>}

        <ul role="listbox" aria-label="Companies" aria-busy={loading}>
          {options.map(manufacturer_0 => {
          const id_1 = String(manufacturer_0._id);
          const added = addedIds.has(id_1);
          return <li key={id_1}>
                <div className={`flex items-center gap-2 px-3 py-2 text-sm ${added ? "" : "hover:bg-gray-50"}`}>
                  <span title={manufacturer_0.name} className="flex-1 min-w-0 truncate capitalize text-gray-800">{manufacturer_0.name}</span>
                  {added ? <span className="flex items-center gap-1 flex-shrink-0 text-xs font-medium text-[#354EF0]">
                      <Check size={14} /> Added
                    </span> : <button type="button" onClick={() => handleAdd(manufacturer_0)} aria-label={`Add ${manufacturer_0.name}`} className="flex-shrink-0 px-2.5 py-1 rounded-md border border-[#354EF0] text-xs font-medium text-[#354EF0] hover:bg-[#354EF0] hover:text-white">
                      Add
                    </button>}
                </div>
              </li>;
        })}
        </ul>
        {page < totalPages && !failure && <div ref={sentinelRef} className="h-1" aria-hidden="true" />}
        {loading && <div role="status" aria-label="Loading companies">
            <SkeletonRows count={options.length ? 3 : 8} />
          </div>}
        {!loading && !failure && options.length === 0 && <p className="px-3 py-6 text-center text-sm text-gray-400">
            {search_1 ? "No matching companies found." : "No companies found."}
          </p>}
        {failure && <div className="flex items-center justify-between gap-2 px-3 py-3 text-xs text-red-600" role="alert">
            <span>{failure}</span>
            <button type="button" onClick={() => loadPage(search_1, options.length ? page + 1 : 1)} className="font-medium text-[#354EF0] hover:underline">Retry</button>
          </div>}
      </div>

    </div>;
};
export default RepCompaniesManager;
function _temp(_, index) {
  return <div key={index} className="flex items-center gap-3 px-3 py-2.5"><div className="h-3.5 flex-1 rounded bg-gray-100 animate-pulse" style={{
      maxWidth: `${55 + index * 17 % 35}%`
    }} /><div className="ml-auto h-6 w-12 rounded-md bg-gray-100 animate-pulse" /></div>;
}